import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUsers, 
  faUserPlus, 
  faComments, 
  faDatabase, 
  faRobot, 
  faCheck, 
  faEdit, 
  faSave, 
  faTimes, 
  faThumbsUp, 
  faExpandArrowsAlt,
  faEye,
  faUser,
  faEnvelope,
  faCalendarAlt,
  faClipboardList,
  faComment,
  faSearch,
  faDownload,
  faFileExport,
  faHistory,
  faChevronDown,
  faChevronUp,
  faNotesMedical,
  faSpinner,
  faUserMd,
  faClock,
  faKey,
  faRedo
} from '@fortawesome/free-solid-svg-icons';
import { adminAPI, authAPI } from '../../services/api';
import { Skeleton } from '../Skeleton';
import { ClinicalHistoryEntry } from '../../types/clinical';
import './PatientManagement.css';

interface Patient {
  id: number;
  email: string;
  full_name: string;
  clinical_info?: string;
  created_at: string;
  is_active: boolean;
  is_invited?: boolean; // Whether patient has been invited
  activation_token?: string; // Added for newly created patients
  has_consent?: boolean; // Whether patient has signed consent
  doctor_id?: number; // Assigned doctor ID
  doctor_name?: string; // Assigned doctor name
  activation_token_expires_at?: string; // When activation token expires
  activation_token_expired?: boolean; // Whether activation token is expired
  is_suspended?: boolean; // Whether account is suspended
}

interface Conversation {
  id: number;
  question: string;
  rag_answer: string;
  basic_answer: string;
  selected_answer?: string;
  rag_sources?: string;
  rag_prompt?: string;
  basic_prompt?: string;
  feedback?: string;
  created_at: string;
}

export const PatientManagement: React.FC = () => {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [expandedConversation, setExpandedConversation] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  
  // Clinical info editing
  const [editingClinicalInfo, setEditingClinicalInfo] = useState(false);
  const [clinicalInfoValue, setClinicalInfoValue] = useState('');
  const [clinicalInfoSaving, setClinicalInfoSaving] = useState(false);
  const [clinicalNotes, setClinicalNotes] = useState('');
  const [aiImprovementLoading, setAiImprovementLoading] = useState(false);
  const [aiImprovedText, setAiImprovedText] = useState('');
  const [showAiPreview, setShowAiPreview] = useState(false);
  const [showMarkdownPreview, setShowMarkdownPreview] = useState(false);

  // Clinical history
  const [clinicalHistory, setClinicalHistory] = useState<ClinicalHistoryEntry[]>([]);
  const [showClinicalHistory, setShowClinicalHistory] = useState(false);
  const [clinicalHistoryLoading, setClinicalHistoryLoading] = useState(false);

  // Search functionality
  const [searchTerm, setSearchTerm] = useState('');

  // Export functionality
  const [exporting, setExporting] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  // New patient form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newPatient, setNewPatient] = useState({
    email: '',
    full_name: '',
    clinical_info: '',
    doctor_id: undefined as number | undefined
  });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [newPatientAiImprovement, setNewPatientAiImprovement] = useState(false);
  const [newPatientAiImprovedText, setNewPatientAiImprovedText] = useState('');
  const [showNewPatientAiPreview, setShowNewPatientAiPreview] = useState(false);
  
  // Doctor management
  const [doctors, setDoctors] = useState<any[]>([]);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  
  // PDF Invitation Popup
  const [showPDFInvitationPopup, setShowPDFInvitationPopup] = useState(false);
  const [pdfInvitationUrl, setPDFInvitationUrl] = useState<string>('');
  const [createdPatientInfo, setCreatedPatientInfo] = useState<{id: number, name: string} | null>(null);

  // PDF Viewer Popups for existing patients
  const [showPDFViewerPopup, setShowPDFViewerPopup] = useState(false);
  const [pdfViewerUrl, setPDFViewerUrl] = useState<string>('');
  const [pdfViewerInfo, setPDFViewerInfo] = useState<{type: 'invitation' | 'consent', patientName: string} | null>(null);

  // Token regeneration
  const [regeneratingToken, setRegeneratingToken] = useState<number | null>(null);

  // Account suspension
  const [suspendingAccount, setSuspendingAccount] = useState<number | null>(null);
  const [resendingInvitation, setResendingInvitation] = useState<number | null>(null);

  const loadDoctors = useCallback(async () => {
    setLoadingDoctors(true);
    try {
      const doctorsData = await adminAPI.getDoctors();
      setDoctors(doctorsData);
    } catch (error) {
      console.error('Error loading doctors:', error);
    } finally {
      setLoadingDoctors(false);
    }
  }, []);

  const loadCurrentUser = useCallback(async () => {
    try {
      const userData = await authAPI.getCurrentUser();
      setCurrentUser(userData);
      
      // If admin, also load doctors list
      if (userData.is_admin) {
        loadDoctors();
      }
    } catch (error) {
      console.error('Error loading current user:', error);
    }
  }, [loadDoctors]);

  useEffect(() => {
    loadPatients();
    loadCurrentUser();
  }, [loadCurrentUser]);

  const loadPatients = async () => {
    setLoading(true);
    try {
      const data = await adminAPI.getPatients();
      setPatients(data);
    } catch (error) {
      console.error('Error loading patients:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadConversations = async (patientId: number) => {
    setConversationsLoading(true);
    try {
      const data = await adminAPI.getPatientConversations(patientId);
      setConversations(data.conversations);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setConversationsLoading(false);
    }
  };

  const loadClinicalHistory = async (patientId: number) => {
    setClinicalHistoryLoading(true);
    try {
      const history = await adminAPI.getUserClinicalHistory(patientId);
      setClinicalHistory(history);
    } catch (error) {
      console.error('Error loading clinical history:', error);
      setClinicalHistory([]);
    } finally {
      setClinicalHistoryLoading(false);
    }
  };

  const handlePatientSelect = (patient: Patient) => {
    setSelectedPatient(patient);
    setExpandedConversation(null);
    setClinicalInfoValue(patient.clinical_info || '');
    setEditingClinicalInfo(false);
    setClinicalNotes('');
    setShowAiPreview(false);
    setAiImprovedText('');
    setShowMarkdownPreview(false);
    setShowClinicalHistory(false);
    setClinicalHistory([]);
    loadConversations(patient.id);
  };

  const handleUpdateClinicalInfo = async () => {
    if (!selectedPatient) return;
    
    setClinicalInfoSaving(true);
    try {
      // Send the clinical info as-is (already AI-improved if user chose that option)
      await adminAPI.addClinicalHistoryEntry(selectedPatient.id, clinicalInfoValue, clinicalNotes, false);
      
      // Update the patient in the list
      setPatients(prev => prev.map(p => 
        p.id === selectedPatient.id 
          ? { ...p, clinical_info: clinicalInfoValue }
          : p
      ));
      setSelectedPatient(prev => prev ? { ...prev, clinical_info: clinicalInfoValue } : null);
    setEditingClinicalInfo(false);
    setClinicalNotes('');
    setShowAiPreview(false);
    setAiImprovedText('');      // Refresh clinical history if it's being shown
      if (showClinicalHistory) {
        loadClinicalHistory(selectedPatient.id);
      }
    } catch (error) {
      console.error('Error updating clinical info:', error);
    } finally {
      setClinicalInfoSaving(false);
    }
  };

  const handlePreviewAiImprovement = async () => {
    if (!clinicalInfoValue.trim()) return;
    
    setAiImprovementLoading(true);
    try {
      const result = await adminAPI.previewAIImprovement(clinicalInfoValue);
      if (result.improved) {
        setAiImprovedText(result.improved);
        setShowAiPreview(true);
      }
    } catch (error) {
      console.error('Error previewing AI improvement:', error);
    } finally {
      setAiImprovementLoading(false);
    }
  };

  const handleAcceptAiImprovement = () => {
    setClinicalInfoValue(aiImprovedText);
    setShowAiPreview(false);
  };

  const handleRejectAiImprovement = () => {
    setShowAiPreview(false);
    setAiImprovedText('');
  };

  const handleNewPatientAiToggle = async (enabled: boolean) => {
    if (!enabled) {
      return;
    }
    
    // User clicked the button - trigger AI improvement
    if (newPatient.clinical_info.trim()) {
      setAiImprovementLoading(true);
      try {
        const result = await adminAPI.previewAIImprovement(newPatient.clinical_info);
        if (result.improved) {
          setNewPatientAiImprovedText(result.improved);
          setShowNewPatientAiPreview(true);
        }
      } catch (error) {
        console.error('Error auto-generating AI improvement for new patient:', error);
      } finally {
        setAiImprovementLoading(false);
      }
    }
  };

  const handleAcceptNewPatientAiImprovement = () => {
    setNewPatient(prev => ({ ...prev, clinical_info: newPatientAiImprovedText }));
    setNewPatientAiImprovement(true);
    setShowNewPatientAiPreview(false);
  };

  const handleRejectNewPatientAiImprovement = () => {
    setShowNewPatientAiPreview(false);
    setNewPatientAiImprovedText('');
  };

  const handleToggleClinicalHistory = () => {
    if (!selectedPatient) return;
    
    setShowClinicalHistory(!showClinicalHistory);
    if (!showClinicalHistory && clinicalHistory.length === 0) {
      loadClinicalHistory(selectedPatient.id);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  const handleCreatePatient = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Prepare patient data
      const patientData = { ...newPatient };
      
      // Auto-assign to current doctor if they're a doctor and no doctor is selected
      if (currentUser?.is_doctor && !patientData.doctor_id) {
        patientData.doctor_id = currentUser.id;
      }
      
      // First create the patient
      const createdPatient = await adminAPI.createPatientWithInvite(patientData);
      
      // Clear form and close modal
      resetNewPatientForm();
      setShowCreateForm(false);
      
      // Reload patients list
      await loadPatients();
      
      // Generate and show PDF invitation popup
      try {
        const pdfBlob = await adminAPI.downloadInvitationPDF(createdPatient.id);
        const url = URL.createObjectURL(pdfBlob);
        setPDFInvitationUrl(url);
        setCreatedPatientInfo({ id: createdPatient.id, name: createdPatient.full_name });
        setShowPDFInvitationPopup(true);
      } catch (pdfError) {
        console.error('Error generating invitation PDF:', pdfError);
        // Show success message without alert popup - user can get PDF from patient list
        console.log('Zaproszenie zostało wysłane na podany adres email. PDF można pobrać z listy pacjentów.');
      }
    } catch (error) {
      console.error('Error creating patient:', error);
      alert('Błąd podczas tworzenia pacjenta. Sprawdź czy adres email nie jest już używany.');
    }
  };

  const resetNewPatientForm = () => {
    setNewPatient({ email: '', full_name: '', clinical_info: '', doctor_id: undefined });
    setNewPatientAiImprovement(false);
    setShowNewPatientAiPreview(false);
    setNewPatientAiImprovedText('');
  };

  const closePDFInvitationPopup = () => {
    if (pdfInvitationUrl) {
      URL.revokeObjectURL(pdfInvitationUrl);
    }
    setPDFInvitationUrl('');
    setCreatedPatientInfo(null);
    setShowPDFInvitationPopup(false);
  };

  const printPDFInvitation = () => {
    if (pdfInvitationUrl) {
      const printWindow = window.open(pdfInvitationUrl, '_blank');
      if (printWindow) {
        printWindow.focus();
        printWindow.print();
      }
    }
  };

  const downloadPDFInvitation = () => {
    if (pdfInvitationUrl && createdPatientInfo) {
      const a = document.createElement('a');
      a.href = pdfInvitationUrl;
      a.download = `zaproszenie_${createdPatientInfo.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const getSelectedAnswerBadge = (selected?: string) => {
    if (!selected) return <span className="badge not-selected"><FontAwesomeIcon icon={faTimes} /> Nie wybrano</span>;
    if (selected === 'rag') return <span className="badge rag-selected"><FontAwesomeIcon icon={faDatabase} /> Z bazą wiedzy</span>;
    if (selected === 'basic') return <span className="badge basic-selected"><FontAwesomeIcon icon={faRobot} /> Podstawowa</span>;
    return <span className="badge not-selected"><FontAwesomeIcon icon={faTimes} /> Nie wybrano</span>;
  };

  // Filter patients based on search term and sort by creation date (newest first)
  const filteredPatients = patients
    .filter(patient => 
      patient.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      patient.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (patient.clinical_info && patient.clinical_info.toLowerCase().includes(searchTerm.toLowerCase()))
    )
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  // Export functions
  const exportPatientsData = async () => {
    setExporting(true);
    try {
      const data = {
        patients: patients.map(p => ({
          id: p.id,
          email: p.email,
          full_name: p.full_name,
          clinical_info: p.clinical_info,
          created_at: p.created_at,
          is_active: p.is_active
        })),
        export_date: new Date().toISOString(),
        total_patients: patients.length
      };
      
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `patients_data_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting patients data:', error);
    } finally {
      setExporting(false);
    }
  };

  const exportConversationsData = async () => {
    if (!selectedPatient) return;
    
    setExporting(true);
    try {
      const data = {
        patient: {
          id: selectedPatient.id,
          full_name: selectedPatient.full_name,
          email: selectedPatient.email,
          clinical_info: selectedPatient.clinical_info
        },
        conversations: conversations.map(conv => ({
          id: conv.id,
          question: conv.question,
          rag_answer: conv.rag_answer,
          basic_answer: conv.basic_answer,
          selected_answer: conv.selected_answer,
          rag_prompt: conv.rag_prompt,
          feedback: conv.feedback,
          created_at: conv.created_at
        })),
        export_date: new Date().toISOString(),
        total_conversations: conversations.length
      };
      
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `conversations_${selectedPatient.full_name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting conversations data:', error);
    } finally {
      setExporting(false);
    }
  };

  const exportAllData = async () => {
    setExporting(true);
    try {
      const allConversations = [];
      
      // Collect conversations for all patients
      for (const patient of patients) {
        try {
          const patientConversations = await adminAPI.getPatientConversations(patient.id);
          allConversations.push({
            patient: {
              id: patient.id,
              full_name: patient.full_name,
              email: patient.email,
              clinical_info: patient.clinical_info
            },
            conversations: patientConversations.conversations
          });
        } catch (error) {
          console.error(`Error loading conversations for patient ${patient.id}:`, error);
        }
      }
      
      const data = {
        patients: patients,
        patient_conversations: allConversations,
        export_date: new Date().toISOString(),
        statistics: {
          total_patients: patients.length,
          total_conversations: allConversations.reduce((sum, pc) => sum + pc.conversations.length, 0),
          patients_with_conversations: allConversations.filter(pc => pc.conversations.length > 0).length
        }
      };
      
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `complete_data_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting all data:', error);
    } finally {
      setExporting(false);
    }
  };

  // PDF Download functions - now show in popup viewer
  const viewInvitationPDF = async (patientId: number, patientName: string) => {
    setDownloadingPDF(true);
    try {
      const pdfBlob = await adminAPI.downloadInvitationPDF(patientId);
      const url = URL.createObjectURL(pdfBlob);
      setPDFViewerUrl(url);
      setPDFViewerInfo({ type: 'invitation', patientName });
      setShowPDFViewerPopup(true);
    } catch (error) {
      console.error('Error loading invitation PDF:', error);
      alert('Błąd podczas ładowania zaproszenia PDF');
    } finally {
      setDownloadingPDF(false);
    }
  };

  const viewConsentPDF = async (patientId: number, patientName: string) => {
    setDownloadingPDF(true);
    try {
      const pdfBlob = await adminAPI.downloadConsentPDF(patientId);
      const url = URL.createObjectURL(pdfBlob);
      setPDFViewerUrl(url);
      setPDFViewerInfo({ type: 'consent', patientName });
      setShowPDFViewerPopup(true);
    } catch (error) {
      console.error('Error loading consent PDF:', error);
      alert('Błąd podczas ładowania zgody PDF');
    } finally {
      setDownloadingPDF(false);
    }
  };

  const closePDFViewerPopup = () => {
    if (pdfViewerUrl) {
      URL.revokeObjectURL(pdfViewerUrl);
    }
    setPDFViewerUrl('');
    setPDFViewerInfo(null);
    setShowPDFViewerPopup(false);
  };

  const downloadCurrentPDF = () => {
    if (pdfViewerUrl && pdfViewerInfo) {
      const a = document.createElement('a');
      a.href = pdfViewerUrl;
      const fileName = pdfViewerInfo.type === 'invitation' 
        ? `zaproszenie_${pdfViewerInfo.patientName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`
        : `zgoda_${pdfViewerInfo.patientName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const printCurrentPDF = () => {
    if (pdfViewerUrl) {
      const printWindow = window.open(pdfViewerUrl, '_blank');
      if (printWindow) {
        printWindow.focus();
        printWindow.print();
      }
    }
  };

  const handleRegenerateActivationToken = async (patientId: number, patientName: string) => {
    if (!window.confirm(`Czy na pewno chcesz wygenerować nowy kod aktywacyjny dla pacjenta ${patientName}? Poprzedni kod zostanie unieważniony.`)) {
      return;
    }

    setRegeneratingToken(patientId);
    try {
      await adminAPI.regenerateActivationToken(patientId);
      
      // Reload patients list to get updated token expiration info
      await loadPatients();
      
      alert(`Nowy kod aktywacyjny został wygenerowany dla pacjenta ${patientName}. Nowe zaproszenie zostało wysłane na adres email.`);
    } catch (error) {
      console.error('Error regenerating activation token:', error);
      alert('Błąd podczas generowania nowego kodu aktywacyjnego. Spróbuj ponownie.');
    } finally {
      setRegeneratingToken(null);
    }
  };

  const handleResendInvitation = async (patientId: number, patientName: string) => {
    if (!window.confirm(`Czy na pewno chcesz ponownie wysłać zaproszenie dla pacjenta ${patientName}?`)) {
      return;
    }

    setResendingInvitation(patientId);
    try {
      await adminAPI.resendInvitation(patientId);
      
      alert(`Zaproszenie zostało ponownie wysłane na adres email pacjenta ${patientName}.`);
    } catch (error) {
      console.error('Error resending invitation:', error);
      alert('Błąd podczas wysyłania zaproszenia. Spróbuj ponownie.');
    } finally {
      setResendingInvitation(null);
    }
  };

  const handleSuspendAccount = async (patientId: number, patientName: string) => {
    if (!window.confirm(`Czy na pewno chcesz zawiesić konto pacjenta ${patientName}? Pacjent nie będzie mógł się zalogować do momentu odwieszenia konta.`)) {
      return;
    }

    setSuspendingAccount(patientId);
    try {
      await adminAPI.suspendPatient(patientId);
      
      // Reload patients list to get updated suspension status
      await loadPatients();
      
      alert(`Konto pacjenta ${patientName} zostało zawieszone. Pacjent nie będzie mógł się zalogować.`);
    } catch (error) {
      console.error('Error suspending account:', error);
      alert('Błąd podczas zawieszania konta. Spróbuj ponownie.');
    } finally {
      setSuspendingAccount(null);
    }
  };

  const handleUnsuspendAccount = async (patientId: number, patientName: string) => {
    if (!window.confirm(`Czy na pewno chcesz odwiesić konto pacjenta ${patientName}? Pacjent będzie mógł ponownie się zalogować.`)) {
      return;
    }

    setSuspendingAccount(patientId);
    try {
      await adminAPI.unsuspendPatient(patientId);
      
      // Reload patients list to get updated suspension status
      await loadPatients();
      
      alert(`Konto pacjenta ${patientName} zostało odwieszone. Pacjent może ponownie się zalogować.`);
    } catch (error) {
      console.error('Error unsuspending account:', error);
      alert('Błąd podczas odwieszania konta. Spróbuj ponownie.');
    } finally {
      setSuspendingAccount(null);
    }
  };

  return (
    <div className="patient-management">
      <div className="management-header">
        <h1><FontAwesomeIcon icon={faUsers} /> Zarządzanie pacjentami</h1>
        <div className="header-actions">
          <div className="export-buttons">
            <button 
              onClick={exportPatientsData}
              className="export-button"
              disabled={exporting}
              title="Eksportuj dane pacjentów"
            >
              <FontAwesomeIcon icon={faDownload} className="button-icon" />
              {exporting ? 'Eksportowanie...' : 'Eksportuj pacjentów'}
            </button>
            {selectedPatient && (
              <button 
                onClick={exportConversationsData}
                className="export-button"
                disabled={exporting}
                title="Eksportuj rozmowy wybranego pacjenta"
              >
                <FontAwesomeIcon icon={faFileExport} className="button-icon" />
                Eksportuj rozmowy
              </button>
            )}
            <button 
              onClick={exportAllData}
              className="export-button primary"
              disabled={exporting}
              title="Eksportuj wszystkie dane"
            >
              <FontAwesomeIcon icon={faFileExport} className="button-icon" />
              Eksportuj wszystko
            </button>
          </div>
          <button 
            onClick={() => setShowCreateForm(true)}
            className="create-button"
          >
            <FontAwesomeIcon icon={faUserPlus} className="button-icon" />
            Zaproś pacjenta
          </button>
        </div>
      </div>

      {showCreateForm && (
        <div className="modal-overlay">
          <div className="modal card large-modal" role="dialog" aria-modal="true">
            <h2><FontAwesomeIcon icon={faUserPlus} /> Zaproś nowego pacjenta</h2>
            <p className="modal-description">
              Pacjent otrzyma email z linkiem aktywacyjnym, gdzie będzie mógł ustawić swoje hasło.
            </p>
            <form onSubmit={handleCreatePatient}>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="email"><FontAwesomeIcon icon={faEnvelope} /> Email</label>
                  <input 
                    id="email" 
                    type="email" 
                    value={newPatient.email} 
                    onChange={e => setNewPatient({ ...newPatient, email: e.target.value })} 
                    required 
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="full_name"><FontAwesomeIcon icon={faUser} /> Imię i nazwisko</label>
                  <input 
                    id="full_name" 
                    type="text" 
                    value={newPatient.full_name} 
                    onChange={e => setNewPatient({ ...newPatient, full_name: e.target.value })} 
                    required 
                  />
                </div>
              </div>
              
              {/* Doctor Assignment - only show for admins */}
              {currentUser?.is_admin && (
                <div className="form-group">
                  <label htmlFor="doctor_assignment">
                    <FontAwesomeIcon icon={faUserMd} /> Przypisz lekarza (opcjonalne)
                  </label>
                  <select
                    id="doctor_assignment"
                    value={newPatient.doctor_id || ''}
                    onChange={e => setNewPatient({ 
                      ...newPatient, 
                      doctor_id: e.target.value ? parseInt(e.target.value) : undefined 
                    })}
                    disabled={loadingDoctors}
                  >
                    <option value="">-- Nie przypisuj lekarza --</option>
                    {doctors.map(doctor => (
                      <option key={doctor.id} value={doctor.id}>
                        {doctor.full_name} ({doctor.email}) - {doctor.patient_count} pacjentów
                      </option>
                    ))}
                  </select>
                  {loadingDoctors && (
                    <div className="loading-note">
                      <FontAwesomeIcon icon={faSpinner} spin /> Ładowanie listy lekarzy...
                    </div>
                  )}
                </div>
              )}
              
              {/* Auto-assignment info for doctors */}
              {currentUser?.is_doctor && (
                <div className="form-group">
                  <div className="doctor-assignment-info">
                    <FontAwesomeIcon icon={faUserMd} />
                    <span>
                      <strong>Przypisanie lekarza:</strong> Pacjent zostanie automatycznie przypisany do Ciebie ({currentUser.full_name})
                    </span>
                  </div>
                </div>
              )}
              
              <div className="form-group full-width">
                <label htmlFor="clinical_info"><FontAwesomeIcon icon={faClipboardList} /> Informacje kliniczne</label>
                <textarea 
                  id="clinical_info" 
                  value={newPatient.clinical_info} 
                  onChange={e => setNewPatient({ ...newPatient, clinical_info: e.target.value })} 
                  rows={6} 
                  placeholder="Wprowadź informacje kliniczne pacjenta..." 
                  disabled={aiImprovementLoading}
                  className={aiImprovementLoading ? 'processing' : ''}
                />
                {newPatient.clinical_info.trim() && (
                  <div className="ai-improve-suggestion">
                    <div className="suggestion-text">
                      <FontAwesomeIcon icon={faRobot} />
                      <span>Chcesz poprawić i ustandaryzować tekst za pomocą AI?</span>
                    </div>
                    <button 
                      type="button"
                      className="ai-improve-inline-button"
                      onClick={() => handleNewPatientAiToggle(true)}
                      disabled={aiImprovementLoading}
                    >
                      <FontAwesomeIcon icon={aiImprovementLoading ? faSpinner : faRobot} spin={aiImprovementLoading} />
                      {aiImprovementLoading ? 'Przetwarzanie...' : 'Popraw AI'}
                    </button>
                  </div>
                )}
                
                {/* New Patient AI Preview Modal Integration */}
                {showNewPatientAiPreview && (
                  <div className="ai-improvement-notice">
                    <FontAwesomeIcon icon={faCheck} /> 
                    <span>Tekst został poprawiony przez AI. Możesz go dalej edytować przed zapisem.</span>
                  </div>
                )}
              </div>
              
              <div className="modal-buttons">
                <button type="button" onClick={() => { resetNewPatientForm(); setShowCreateForm(false); }} tabIndex={0}>
                  <FontAwesomeIcon icon={faTimes} /> Anuluj
                </button>
                <button type="submit" tabIndex={0}>
                  <FontAwesomeIcon icon={faSave} /> Wyślij zaproszenie
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* AI Preview Modal */}
      {showAiPreview && (
        <div className="modal-overlay">
          <div className="modal card ai-preview-modal" role="dialog" aria-modal="true">
            <div className="ai-preview-header">
              <h2><FontAwesomeIcon icon={faRobot} /> Podgląd poprawionego tekstu</h2>
              <p>AI zaproponowało następujące ulepszenia:</p>
            </div>
            <div className="ai-preview-content">
              <ReactMarkdown>{aiImprovedText}</ReactMarkdown>
            </div>
            <div className="modal-buttons">
              <button 
                type="button" 
                className="reject-ai-button"
                onClick={handleRejectAiImprovement}
              >
                <FontAwesomeIcon icon={faTimes} /> Odrzuć
              </button>
              <button 
                type="button" 
                className="accept-ai-button"
                onClick={handleAcceptAiImprovement}
              >
                <FontAwesomeIcon icon={faCheck} /> Zastosuj poprawki
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Preview Modal for New Patient */}
      {showNewPatientAiPreview && (
        <div className="modal-overlay">
          <div className="modal card ai-preview-modal" role="dialog" aria-modal="true">
            <div className="ai-preview-header">
              <h2><FontAwesomeIcon icon={faRobot} /> Podgląd poprawionego tekstu</h2>
              <p>AI zaproponowało następujące ulepszenia:</p>
            </div>
            <div className="ai-preview-content">
              <ReactMarkdown>{newPatientAiImprovedText}</ReactMarkdown>
            </div>
            <div className="modal-buttons">
              <button 
                type="button" 
                className="reject-ai-button"
                onClick={handleRejectNewPatientAiImprovement}
              >
                <FontAwesomeIcon icon={faTimes} /> Odrzuć
              </button>
              <button 
                type="button" 
                className="accept-ai-button"
                onClick={handleAcceptNewPatientAiImprovement}
              >
                <FontAwesomeIcon icon={faCheck} /> Zastosuj poprawki
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Invitation Popup */}
      {showPDFInvitationPopup && (
        <div className="modal-overlay">
          <div className="modal card pdf-invitation-modal" role="dialog" aria-modal="true">
            <div className="pdf-invitation-header">
              <h2><FontAwesomeIcon icon={faFileExport} /> Zaproszenie gotowe!</h2>
              <p>
                Pacjent <strong>{createdPatientInfo?.name}</strong> został zaproszony. 
                Możesz wydrukować poniższe zaproszenie i przekazać je pacjentowi.
              </p>
            </div>
            <div className="pdf-invitation-content">
              {pdfInvitationUrl && (
                <iframe
                  src={pdfInvitationUrl}
                  className="pdf-preview"
                  title="Podgląd zaproszenia PDF"
                />
              )}
            </div>
            <div className="modal-buttons pdf-modal-buttons">
              <button 
                type="button" 
                className="print-button"
                onClick={printPDFInvitation}
                title="Drukuj zaproszenie"
              >
                <FontAwesomeIcon icon={faFileExport} /> Drukuj
              </button>
              <button 
                type="button" 
                className="download-button"
                onClick={downloadPDFInvitation}
                title="Pobierz plik PDF"
              >
                <FontAwesomeIcon icon={faDownload} /> Pobierz PDF
              </button>
              <button 
                type="button" 
                className="close-button"
                onClick={closePDFInvitationPopup}
                title="Zamknij okno"
              >
                <FontAwesomeIcon icon={faTimes} /> Zamknij
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Viewer Popup for existing patients */}
      {showPDFViewerPopup && (
        <div className="modal-overlay">
          <div className="modal card pdf-invitation-modal" role="dialog" aria-modal="true">
            <div className="pdf-invitation-header">
              <h2><FontAwesomeIcon icon={faEye} /> 
                {pdfViewerInfo?.type === 'invitation' ? 'Ulotka zaproszenia' : 'Zgoda pacjenta'}
              </h2>
              <p>
                {pdfViewerInfo?.type === 'invitation' 
                  ? `Ulotka zaproszenia dla pacjenta ${pdfViewerInfo?.patientName || ''}`
                  : `Podpisana zgoda pacjenta ${pdfViewerInfo?.patientName || ''}`
                }
              </p>
            </div>
            <div className="pdf-invitation-content">
              {pdfViewerUrl && (
                <iframe
                  src={pdfViewerUrl}
                  className="pdf-preview"
                  title={pdfViewerInfo?.type === 'invitation' ? 'Podgląd ulotki zaproszenia' : 'Podgląd zgody pacjenta'}
                />
              )}
            </div>
            <div className="modal-buttons pdf-modal-buttons">
              <button 
                type="button" 
                className="print-button"
                onClick={printCurrentPDF}
                title="Drukuj dokument"
              >
                <FontAwesomeIcon icon={faFileExport} /> Drukuj
              </button>
              <button 
                type="button" 
                className="download-button"
                onClick={downloadCurrentPDF}
                title="Pobierz plik PDF"
              >
                <FontAwesomeIcon icon={faDownload} /> Pobierz PDF
              </button>
              <button 
                type="button" 
                className="close-button"
                onClick={closePDFViewerPopup}
                title="Zamknij okno"
              >
                <FontAwesomeIcon icon={faTimes} /> Zamknij
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="management-content">
        <div className="patients-list panel">
          <h2><FontAwesomeIcon icon={faUsers} /> Lista pacjentów</h2>
          
          {/* Search input */}
          <div className="search-section">
            <div className="search-input-group">
              <FontAwesomeIcon icon={faSearch} className="search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder="Szukaj pacjentów (imię, email, informacje kliniczne)..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button 
                  className="clear-search"
                  onClick={() => setSearchTerm('')}
                  title="Wyczyść wyszukiwanie"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>
            {searchTerm && (
              <div className="search-results-info">
                Znaleziono {filteredPatients.length} z {patients.length} pacjentów
              </div>
            )}
          </div>

          {loading ? (
            <>
              {[...Array(5)].map((_, i) => (
                <div className="patient-card card" key={i} style={{marginBottom:'1rem'}}><Skeleton height="2.2em" /></div>
              ))}
            </>
          ) : (
            filteredPatients.length === 0 ? (
              <div className="no-conversations">
                {searchTerm ? (
                  <>
                    <FontAwesomeIcon icon={faSearch} /> Brak pacjentów pasujących do wyszukiwania "{searchTerm}"
                  </>
                ) : (
                  'Brak pacjentów'
                )}
              </div>
            ) :
            filteredPatients.map(patient => (
              <div
                key={patient.id}
                className={`patient-card card${selectedPatient?.id === patient.id ? ' selected' : ''}`}
                tabIndex={0}
                role="button"
                aria-pressed={selectedPatient?.id === patient.id}
                onClick={() => handlePatientSelect(patient)}
                onKeyPress={e => { if (e.key === 'Enter') handlePatientSelect(patient); }}
              >
                <div className="patient-header">
                  <h3><FontAwesomeIcon icon={faUser} /> {patient.full_name}</h3>
                  <div className="patient-email"><FontAwesomeIcon icon={faEnvelope} /> {patient.email}</div>
                </div>
                <div className="patient-meta">
                  <div className="patient-date">
                    <FontAwesomeIcon icon={faCalendarAlt} /> {formatDate(patient.created_at)}
                  </div>
                  <div className={`patient-doctor ${!patient.doctor_name ? 'unassigned' : ''}`}>
                    <FontAwesomeIcon icon={faUserMd} />
                    {patient.doctor_name || 'Nie przypisano lekarza'}
                  </div>
                  <div className="patient-status">
                    <div className={`consent-status ${patient.has_consent ? 'signed' : 'unsigned'}`}>
                      <FontAwesomeIcon icon={patient.has_consent ? faCheck : faTimes} />
                      {patient.has_consent ? 'Zgoda' : 'Brak zgody'}
                    </div>
                    
                    {/* Show activation token status for invited but not active patients */}
                    {!patient.is_active && patient.activation_token_expires_at && (
                      <div className={`activation-status ${patient.activation_token_expired ? 'expired' : 'valid'}`}>
                        <FontAwesomeIcon icon={patient.activation_token_expired ? faClock : faKey} />
                        {patient.activation_token_expired 
                          ? 'Token wygasł' 
                          : `Token ważny do ${formatDate(patient.activation_token_expires_at)}`
                        }
                      </div>
                    )}

                    {/* Show suspension status */}
                    {patient.is_suspended && (
                      <div className="suspension-status suspended">
                        <FontAwesomeIcon icon={faTimes} />
                        Konto zawieszone
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Add regenerate token button for expired tokens */}
                {!patient.is_active && patient.activation_token_expired && (
                  <div className="patient-actions">
                    <button
                      className="regenerate-token-button"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent card selection
                        handleRegenerateActivationToken(patient.id, patient.full_name);
                      }}
                      disabled={regeneratingToken === patient.id}
                      title="Wygeneruj nowy kod aktywacyjny"
                    >
                      <FontAwesomeIcon icon={regeneratingToken === patient.id ? faSpinner : faRedo} 
                                     spin={regeneratingToken === patient.id} />
                      {regeneratingToken === patient.id ? 'Generowanie...' : 'Nowy kod'}
                    </button>
                  </div>
                )}
                
                {patient.clinical_info && (
                  <div className="clinical-info-preview">
                    <FontAwesomeIcon icon={faClipboardList} />
                    <div className="preview-content">
                      <ReactMarkdown 
                        components={{
                          p: ({children}) => <span>{children}</span>,
                          br: () => <span> </span>,
                          strong: ({children}) => <strong>{children}</strong>,
                          em: ({children}) => <em>{children}</em>,
                          h1: ({children}) => <span className="preview-heading">{children}</span>,
                          h2: ({children}) => <span className="preview-heading">{children}</span>,
                          h3: ({children}) => <span className="preview-heading">{children}</span>,
                          h4: ({children}) => <span className="preview-heading">{children}</span>,
                          h5: ({children}) => <span className="preview-heading">{children}</span>,
                          h6: ({children}) => <span className="preview-heading">{children}</span>,
                          ul: ({children}) => <span>{children}</span>,
                          ol: ({children}) => <span>{children}</span>,
                          li: ({children}) => <span>• {children}</span>
                        }}
                      >
                        {patient.clinical_info.substring(0, 80)}
                      </ReactMarkdown>
                      {patient.clinical_info.length > 80 && '...'}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {selectedPatient && (
          <div className="patient-details-panel panel">
            {/* Clinical Information Section */}
            <div className="clinical-info-section card">
              <div className="clinical-info-header">
                <h3><FontAwesomeIcon icon={faNotesMedical} /> Informacje kliniczne</h3>
                <div className="clinical-info-actions">
                  <button 
                    className="history-button"
                    onClick={handleToggleClinicalHistory}
                    title={showClinicalHistory ? 'Ukryj historię' : 'Pokaż historię zmian'}
                  >
                    <FontAwesomeIcon icon={faHistory} />
                    <FontAwesomeIcon icon={showClinicalHistory ? faChevronUp : faChevronDown} />
                    Historia
                  </button>
                  {!editingClinicalInfo ? (
                    <button 
                      className="edit-button"
                      onClick={() => setEditingClinicalInfo(true)}
                      title="Edytuj informacje kliniczne"
                    >
                      <FontAwesomeIcon icon={faEdit} /> Edytuj
                    </button>
                  ) : (
                    <div className="edit-buttons">
                      <button 
                        className="save-button"
                        onClick={handleUpdateClinicalInfo}
                        disabled={clinicalInfoSaving}
                        title="Zapisz zmiany"
                      >
                        <FontAwesomeIcon icon={faSave} /> {clinicalInfoSaving ? 'Zapisywanie...' : 'Zapisz'}
                      </button>
                      <button 
                        className="ai-improve-button"
                        onClick={handlePreviewAiImprovement}
                        disabled={aiImprovementLoading || !clinicalInfoValue.trim()}
                        title="Popraw tekst za pomocą AI"
                      >
                        <FontAwesomeIcon icon={aiImprovementLoading ? faSpinner : faRobot} spin={aiImprovementLoading} />
                        {aiImprovementLoading ? 'Przetwarzanie...' : 'Popraw AI'}
                      </button>
                      <button 
                        className="cancel-button"
                        onClick={() => {
                          setEditingClinicalInfo(false);
                          setClinicalInfoValue(selectedPatient.clinical_info || '');
                          setClinicalNotes('');
                          setShowAiPreview(false);
                          setAiImprovedText('');
                          setShowMarkdownPreview(false);
                        }}
                        title="Anuluj edycję"
                      >
                        <FontAwesomeIcon icon={faTimes} /> Anuluj
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              {editingClinicalInfo ? (
                <div className="clinical-info-editor-container">
                  <div className="editor-header">
                    <div className="editor-tabs">
                      <button 
                        type="button"
                        className={`editor-tab ${!showMarkdownPreview ? 'active' : ''}`}
                        onClick={() => setShowMarkdownPreview(false)}
                      >
                        <FontAwesomeIcon icon={faEdit} /> Edytuj
                      </button>
                      <button 
                        type="button"
                        className={`editor-tab ${showMarkdownPreview ? 'active' : ''}`}
                        onClick={() => setShowMarkdownPreview(true)}
                      >
                        <FontAwesomeIcon icon={faEye} /> Podgląd
                      </button>
                    </div>
                  </div>
                  
                  {!showMarkdownPreview ? (
                    <textarea
                      className="clinical-info-editor"
                      value={clinicalInfoValue}
                      onChange={e => setClinicalInfoValue(e.target.value)}
                      placeholder="Wprowadź informacje kliniczne pacjenta..."
                      rows={6}
                    />
                  ) : (
                    <div className="clinical-info-preview-editor">
                      {clinicalInfoValue ? (
                        <ReactMarkdown>{clinicalInfoValue}</ReactMarkdown>
                      ) : (
                        <em className="preview-placeholder">Brak treści do podglądu</em>
                      )}
                    </div>
                  )}
                  <div className="clinical-notes-section">
                    <label htmlFor="clinical-notes">
                      <FontAwesomeIcon icon={faEdit} /> Notatka do aktualizacji (opcjonalne)
                    </label>
                    <textarea
                      id="clinical-notes"
                      className="clinical-notes-editor"
                      value={clinicalNotes}
                      onChange={e => setClinicalNotes(e.target.value)}
                      placeholder="Dodaj notatkę opisującą powód zmiany informacji klinicznych..."
                      rows={3}
                    />
                  </div>
                </div>
              ) : (
                <div className="clinical-info-display">
                  {selectedPatient.clinical_info ? (
                    <ReactMarkdown>{selectedPatient.clinical_info}</ReactMarkdown>
                  ) : (
                    <em className="no-clinical-info">Brak informacji klinicznych</em>
                  )}
                </div>
              )}

              {/* Clinical History */}
              {showClinicalHistory && (
                <div className="clinical-history-section">
                  <h4><FontAwesomeIcon icon={faHistory} /> Historia zmian</h4>
                  {clinicalHistoryLoading ? (
                    <div className="loading-history">
                      <Skeleton height="3rem" />
                      <Skeleton height="3rem" />
                    </div>
                  ) : clinicalHistory.length === 0 ? (
                    <div className="no-history">
                      <FontAwesomeIcon icon={faHistory} />
                      <span>Brak historii zmian</span>
                    </div>
                  ) : (
                    <div className="clinical-history">
                      {clinicalHistory.map((entry, index) => (
                        <div key={entry.id} className="history-entry">
                          <div className="history-header-entry">
                            <span className="history-date">
                              <FontAwesomeIcon icon={faCalendarAlt} className="date-icon" />
                              {formatDate(entry.created_at)}
                            </span>
                            <span className="history-author">
                              przez {entry.created_by === 'system_migration' ? 'System' : entry.created_by}
                            </span>
                            {index === 0 && (
                              <span className="current-indicator">Aktualne</span>
                            )}
                          </div>
                          <div className="history-content">
                            {entry.clinical_info}
                          </div>
                          {entry.notes && (
                            <div className="history-notes">
                              <FontAwesomeIcon icon={faNotesMedical} className="notes-icon" />
                              <span className="notes-text">{entry.notes}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* PDF Downloads Section */}
            <div className="pdf-downloads-section card">
              <h3><FontAwesomeIcon icon={faEye} /> Dokumenty PDF</h3>
              <div className="pdf-download-buttons">
                <button 
                  className="pdf-download-button invitation"
                  onClick={() => viewInvitationPDF(selectedPatient.id, selectedPatient.full_name)}
                  disabled={downloadingPDF}
                  title="Zobacz ulotka zaproszenia z kodem aktywacji"
                >
                  <FontAwesomeIcon icon={downloadingPDF ? faSpinner : faEye} spin={downloadingPDF} />
                  Ulotka zaproszenia
                </button>
                <button 
                  className={`pdf-download-button consent ${!selectedPatient.has_consent ? 'disabled' : ''}`}
                  onClick={() => selectedPatient.has_consent && viewConsentPDF(selectedPatient.id, selectedPatient.full_name)}
                  disabled={downloadingPDF || !selectedPatient.has_consent}
                  title={selectedPatient.has_consent ? "Zobacz podpisaną zgodę pacjenta" : "Pacjent nie podpisał jeszcze zgody"}
                >
                  <FontAwesomeIcon icon={downloadingPDF ? faSpinner : faEye} spin={downloadingPDF} />
                  {selectedPatient.has_consent ? 'Zgoda pacjenta' : 'Zgoda - nie podpisana'}
                </button>
              </div>
              <div className="pdf-info">
                <p>
                  <strong>Ulotka zaproszenia:</strong> Zawiera instrukcje dla pacjenta, kod QR oraz 8-cyfrowy kod aktywacji. 
                  Kliknij aby zobaczyć w przeglądarce.
                </p>
                <p>
                  <strong>Zgoda pacjenta:</strong> {selectedPatient.has_consent 
                    ? 'Podpisana elektronicznie zgoda na udział w badaniu. Kliknij aby zobaczyć dokument.' 
                    : 'Pacjent nie podpisał jeszcze zgody. Dokument będzie dostępny po aktywacji konta i podpisaniu zgody.'}
                </p>
              </div>
            </div>

            {/* Conversations Section */}
            <div className="conversations-section">
              <h2><FontAwesomeIcon icon={faComments} /> Rozmowy pacjenta</h2>
              {conversationsLoading ? (
                <>
                  {[...Array(3)].map((_, i) => (
                    <div className="conversation-item card" key={i} style={{marginBottom:'1rem'}}><Skeleton height="2em" /></div>
                  ))}
                </>
              ) : conversations.length === 0 ? (
                <div className="no-conversations">
                  <FontAwesomeIcon icon={faComment} /> Brak rozmów
                </div>
              ) : (
                conversations.map(conv => (
                  <div
                    key={conv.id}
                    className={`conversation-item card${expandedConversation === conv.id ? ' expanded' : ''}`}
                    tabIndex={0}
                    role="button"
                    aria-pressed={expandedConversation === conv.id}
                    onClick={() => setExpandedConversation(expandedConversation === conv.id ? null : conv.id)}
                    onKeyPress={e => { if (e.key === 'Enter') setExpandedConversation(expandedConversation === conv.id ? null : conv.id); }}
                    style={{marginBottom:'1rem'}}
                  >
                    <div className="conversation-header">
                      <div className="conversation-info">
                        <span className="question">
                          <FontAwesomeIcon icon={faComment} /> {conv.question}
                        </span>
                        <span className="conversation-date">
                          <FontAwesomeIcon icon={faCalendarAlt} /> {formatDate(conv.created_at)}
                        </span>
                      </div>
                      <div className="conversation-badges">
                        {getSelectedAnswerBadge(conv.selected_answer)}
                        <button className="expand-button" title={expandedConversation === conv.id ? 'Zwiń szczegóły' : 'Rozwiń szczegóły'}>
                          <FontAwesomeIcon icon={faExpandArrowsAlt} />
                        </button>
                      </div>
                    </div>
                    {expandedConversation === conv.id && (
                      <div className="conversation-details">
                        {/* Clinical Info Context */}
                        {selectedPatient.clinical_info && (
                          <div className="context-section card">
                            <h4><FontAwesomeIcon icon={faClipboardList} /> Kontekst kliniczny</h4>
                            <div className="clinical-context">
                              {selectedPatient.clinical_info}
                            </div>
                          </div>
                        )}

                        {/* RAG Sources and Prompts */}
                        {conv.rag_prompt && (
                          <div className="rag-details card">
                            <h4><FontAwesomeIcon icon={faDatabase} /> Szczegóły RAG</h4>
                            <div className="prompt-section">
                              <h5>Prompt RAG:</h5>
                              <div className="prompt-content">
                                <ReactMarkdown 
                                  components={{
                                    p: ({children}) => <p>{children}</p>,
                                    br: () => <br />,
                                  }}
                                >
                                  {conv.rag_prompt}
                                </ReactMarkdown>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Answers Comparison */}
                        <div className="answers-comparison">
                          <div className={`answer-section card ${conv.selected_answer === 'rag' ? 'selected-answer' : ''}`}>
                            <div className="answer-header">
                              <h4><FontAwesomeIcon icon={faDatabase} /> Odpowiedź z bazą wiedzy</h4>
                              {conv.selected_answer === 'rag' && (
                                <span className="selected-badge">
                                  <FontAwesomeIcon icon={faCheck} /> Wybrana
                                </span>
                              )}
                            </div>
                            <div className="answer-content">
                              <ReactMarkdown 
                                components={{
                                  p: ({children}) => <p>{children}</p>,
                                  br: () => <br />,
                                }}
                              >
                                {conv.rag_answer}
                              </ReactMarkdown>
                            </div>
                          </div>
                          
                          <div className={`answer-section card ${conv.selected_answer === 'basic' ? 'selected-answer' : ''}`}>
                            <div className="answer-header">
                              <h4><FontAwesomeIcon icon={faRobot} /> Odpowiedź podstawowa</h4>
                              {conv.selected_answer === 'basic' && (
                                <span className="selected-badge">
                                  <FontAwesomeIcon icon={faCheck} /> Wybrana
                                </span>
                              )}
                            </div>
                            <div className="answer-content">
                              <ReactMarkdown 
                                components={{
                                  p: ({children}) => <p>{children}</p>,
                                  br: () => <br />,
                                }}
                              >
                                {conv.basic_answer}
                              </ReactMarkdown>
                            </div>
                          </div>
                        </div>

                        {/* Feedback Section */}
                        {conv.feedback && (
                          <div className="feedback-section card">
                            <h4><FontAwesomeIcon icon={faThumbsUp} /> Opinia pacjenta</h4>
                            <div className="feedback-content">
                              {conv.feedback}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Patient Management Actions Section */}
            <div className="patient-management-actions card">
              <h3><FontAwesomeIcon icon={faUser} /> Akcje pacjenta</h3>
              <div className="management-actions-grid">
                {/* Resend invitation - only for invited but not active patients */}
                {selectedPatient.is_invited && !selectedPatient.is_active && (
                  <button
                    className="action-button resend-invitation"
                    onClick={() => handleResendInvitation(selectedPatient.id, selectedPatient.full_name)}
                    disabled={resendingInvitation === selectedPatient.id}
                    title="Wyślij ponownie zaproszenie na email"
                  >
                    <FontAwesomeIcon icon={resendingInvitation === selectedPatient.id ? faSpinner : faEnvelope} 
                                   spin={resendingInvitation === selectedPatient.id} />
                    {resendingInvitation === selectedPatient.id ? 'Wysyłanie...' : 'Wyślij zaproszenie ponownie'}
                  </button>
                )}

                {/* Suspend/Unsuspend account - only for active patients */}
                {selectedPatient.is_active && (
                  <>
                    {!selectedPatient.is_suspended ? (
                      <button
                        className="action-button suspend-account danger"
                        onClick={() => handleSuspendAccount(selectedPatient.id, selectedPatient.full_name)}
                        disabled={suspendingAccount === selectedPatient.id}
                        title="Zawieś konto pacjenta - nie będzie mógł się zalogować"
                      >
                        <FontAwesomeIcon icon={suspendingAccount === selectedPatient.id ? faSpinner : faTimes} 
                                       spin={suspendingAccount === selectedPatient.id} />
                        {suspendingAccount === selectedPatient.id ? 'Zawieszanie...' : 'Zawieś konto'}
                      </button>
                    ) : (
                      <button
                        className="action-button unsuspend-account success"
                        onClick={() => handleUnsuspendAccount(selectedPatient.id, selectedPatient.full_name)}
                        disabled={suspendingAccount === selectedPatient.id}
                        title="Odwieś konto pacjenta - będzie mógł się zalogować"
                      >
                        <FontAwesomeIcon icon={suspendingAccount === selectedPatient.id ? faSpinner : faCheck} 
                                       spin={suspendingAccount === selectedPatient.id} />
                        {suspendingAccount === selectedPatient.id ? 'Odwieszanie...' : 'Odwieś konto'}
                      </button>
                    )}
                  </>
                )}
              </div>
              <div className="management-actions-info">
                <p>
                  <strong>Wyślij zaproszenie ponownie:</strong> Wysyła ponownie email z zaproszeniem i instrukcjami aktywacji.
                </p>
                <p>
                  <strong>Zawieś konto:</strong> Zawiesza konto pacjenta - nie będzie mógł się zalogować. 
                  Pacjent otrzyma informację o zawieszeniu i instrukcje kontaktu z administratorem.
                </p>
                <p>
                  <strong>Odwieś konto:</strong> Przywraca możliwość logowania dla zawieszonego pacjenta.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
